/**
 * Custom HorizontalBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { formatStat, formatDecimalWithCommas } from '@shared-lib/utils/format.utils';
import { HorizontalBarChartComponent as ParentHorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { DataErrorComponent } from '@shared-lib/global-components/data-error/data-error.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';

@Component({
  selector: 'app-custom-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrls: ['./horizontal-bar-chart.component.scss']
})
export class CustomHorizontalBarChartComponent extends ParentHorizontalBarChartComponent {
  // Constructor is inherited from parent to avoid dependency injection issues
  displayData: { category: string, value: number, percentage: string, showBox: boolean }[] = [];
  boxYPositions: Record<string, number> = {};
  hasPixelBoxPositions = false;
  noteBoxStyle: any = {};
  private responsiveBindingsInitialized = false;
  private router = inject(Router);

  private getSelectedEngineKeyFromNav(): string {
    const navState = (window?.history?.state || {}) as any;
    return `${navState?.selectedEngineModelKey ?? navState?.engine_model_key ?? navState?.engine_model ?? navState?.selectedEngine ?? ''}`.trim();
  }

  private formatFleetEngineModel(value: string): string {
    const cleaned = `${value || ''}`
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^%+|%+$/g, '');

    return cleaned ? `%${cleaned}%` : '';
  }

  private normalizeEngineKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private isFleetSummaryKey(key: string): boolean {
    const normalized = this.normalizeEngineKey(key);
    return normalized === 'total_no_engines' ||
      normalized === 'new' ||
      normalized === 'new_engines' ||
      normalized === 'rebuild' ||
      normalized === 'rebuilt' ||
      normalized === 'rebuilt_engines';
  }

  private get isEngineDowntimeChart(): boolean {
    const customData = this.data;
    const ep = customData?.customPayload?.endpoints || customData?.endpoints || customData;
    if (Array.isArray(ep)) {
      return ep.some((e: any) => e.functionName === 'FetchEngineDowntime');
    } else if (ep?.functionName === 'FetchEngineDowntime') {
      return true;
    }
    return false;
  }

  private splitEngineEmission(value: string): { engineModel: string; emission: string } {
    const raw = `${value || ''}`.trim().replace(/^%+|%+$/g, '');
    if (!raw) {
      return { engineModel: '', emission: '' };
    }

    // Keep only the first engine entry when labels contain combined values (e.g. "A / B").
    const primaryEntry = raw.split('/').map((part) => part.trim()).filter(Boolean)[0] || raw;
    const normalized = primaryEntry.replace(/\s+/g, ' ').trim();

    const emissionMatch = normalized.match(/(?:T\s*\d+(?:\s+Non\s+Certified)?|Non\s+Certified)/i);
    const emission = this.normalizeEmissionLabel(emissionMatch ? emissionMatch[0] : '');

    let engineModel = '';
    if (emissionMatch && typeof emissionMatch.index === 'number') {
      const beforeEmission = normalized.slice(0, emissionMatch.index).replace(/[,\-\s]+$/g, '').trim();
      if (beforeEmission) {
        engineModel = beforeEmission.split(',')[0].trim();
      }
    }

    if (!engineModel) {
      engineModel = normalized.split(',')[0].trim();
    }
    if (!engineModel) {
      engineModel = normalized;
    }

    return { engineModel, emission };
  }

  private normalizeEmissionLabel(value: string): string {
    const raw = `${value || ''}`.trim().replace(/\s+/g, ' ');
    if (!raw) return '';

    const hasNonCertified = /non\s+certified/i.test(raw);
    const tLevelMatch = raw.match(/t\s*(\d+)/i);

    if (tLevelMatch) {
      return `T${tLevelMatch[1]}${hasNonCertified ? ' Non Certified' : ''}`;
    }

    if (hasNonCertified) {
      return 'Non Certified';
    }

    return raw;
  }

  private resolveEngineDowntimeRecords(res: any): { average: number | null; data: any[] } {
    const responseData = res?.responseList?.[0]?.responseData?.[0] || res?.responseData?.[0] || res;
    return {
      average: typeof responseData?.average_unavailability_percentage === 'number'
        ? responseData.average_unavailability_percentage
        : null,
      data: Array.isArray(responseData?.data) ? responseData.data : []
    };
  }

  private resolveMonthSortMeta(rawCategory: string): { month: number; year: number } | null {
    const value = `${rawCategory || ''}`.trim();
    if (!value) return null;

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return { month: date.getMonth(), year: date.getFullYear() };
    }

    const normalized = value.toLowerCase();
    const monthMap: Record<string, number> = {
      jan: 0, january: 0,
      feb: 1, february: 1,
      mar: 2, march: 2,
      apr: 3, april: 3,
      may: 4,
      jun: 5, june: 5,
      jul: 6, july: 6,
      aug: 7, august: 7,
      sep: 8, sept: 8, september: 8,
      oct: 9, october: 9,
      nov: 10, november: 10,
      dec: 11, december: 11
    };

    const monthMatch = normalized.match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i);
    if (!monthMatch) return null;

    const month = monthMap[monthMatch[1].toLowerCase()];
    if (month === undefined) return null;

    const yearMatch = normalized.match(/\b(19|20)\d{2}\b/);
    const year = yearMatch ? Number(yearMatch[0]) : 0;
    return { month, year };
  }

  private sortEngineDowntimeByMonth(categories: string[], values: number[]): { categories: string[]; values: number[] } {
    const rows = categories.map((category, index) => ({
      category,
      value: values[index],
      sortMeta: this.resolveMonthSortMeta(category),
      originalIndex: index
    }));

    rows.sort((a, b) => {
      if (a.sortMeta && b.sortMeta) {
        if (a.sortMeta.year !== b.sortMeta.year) {
          return a.sortMeta.year - b.sortMeta.year;
        }
        if (a.sortMeta.month !== b.sortMeta.month) {
          return a.sortMeta.month - b.sortMeta.month;
        }
      } else if (a.sortMeta && !b.sortMeta) {
        return -1;
      } else if (!a.sortMeta && b.sortMeta) {
        return 1;
      }

      return a.originalIndex - b.originalIndex;
    });

    return {
      categories: rows.map(row => row.category),
      values: rows.map(row => row.value)
    };
  }

  private formatPercentageLabel(value: number): string {
    if (!Number.isFinite(value)) {
      return '0%';
    }
    const normalized = Number(value.toFixed(2));
    return `${normalized}%`;
  }

  private getEngineUptimeAxisLabel(): string {
    return 'Downtime%';
  }

  private getEngineUptimeAverageLabel(): string {
    const axisLabel = this.getEngineUptimeAxisLabel().replace(/\s+/g, ' ').trim();
    return axisLabel ? `Average ${axisLabel}` : 'Average Downtime% Change';
  }

  private getFleetLifeStageOrder(category: string): number {
    const normalized = this.normalizeEngineKey(category);
    
    // Define the expected order for Fleet Life Stage categories (engine run hours)
    // Support multiple range formats from backend
    const orderMap: Record<string, number> = {
      '<3k': 1,
      '3k-6k': 2,
      '3k-8k': 2,
      '6k-11k': 3,
      '8k-11k': 3,
      '11k-15k': 4,
      '11k-16k': 4,
      '15k-20k': 5,
      '>20k': 6
    };
    
    const order = orderMap[normalized] !== undefined ? orderMap[normalized] : 999;
    console.log('[CUSTOM HBC] getFleetLifeStageOrder:', { category, normalized, order });
    return order;
  }

  private sortFleetLifeStageCategories(categories: string[], values: number[]): { categories: string[], values: number[] } {
    // Create array of tuples to keep categories and values in sync
    const pairs = categories.map((cat, idx) => ({ category: cat, value: values[idx] }));
    
    // Sort by Fleet Life Stage order
    pairs.sort((a, b) => {
      const orderA = this.getFleetLifeStageOrder(a.category);
      const orderB = this.getFleetLifeStageOrder(b.category);
      return orderA - orderB;
    });
    
    // Unzip back into separate arrays
    return {
      categories: pairs.map(p => p.category),
      values: pairs.map(p => p.value)
    };
  }

  private resolveFleetLifeRecord(res: any): any {
    // Unwrap nested responseList from backend
    if (res && !Array.isArray(res) && Array.isArray(res.responseList)) {
      let current = res;
      while (current && Array.isArray(current.responseList) && current.responseList.length > 0) {
        current = current.responseList[0];
      }
      // After unwrapping, check if we have a data array or a single object
      if (current && Array.isArray(current.data)) {
        res = current.data;
      } else if (current && (current.groupData || current.engine_name)) {
        // For FailureDetails responses: { engine_name, groupData }
        res = current;
      } else {
        res = current;
      }
    }

    if (!Array.isArray(res)) {
      return res;
    }

    const selectedEngine = this.normalizeEngineKey(this.getSelectedEngineKeyFromNav());
    if (!selectedEngine) {
      return res[0];
    }

    const exactMatch = res.find((item: any) => this.normalizeEngineKey(item?.engine_name) === selectedEngine);
    if (exactMatch) {
      return exactMatch;
    }

    const baseEngine = selectedEngine.split(',')[0].trim();
    const prefixMatch = res.find((item: any) => this.normalizeEngineKey(item?.engine_name).startsWith(`${baseEngine},`));
    return prefixMatch || res[0];
  }

  private get isFailureDetailsChart(): boolean {
    const customData = this.data;
    const ep = customData?.customPayload?.endpoints || customData?.endpoints || customData;
    if (Array.isArray(ep)) {
      return ep.some((e: any) => e.functionName === 'FailureDetails');
    } else if (ep?.functionName === 'FailureDetails') {
      return true;
    }
    return false;
  }

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService
  ) {
    super(configService, globalFilterService, chartFilterHandler);
  }

  override ngAfterViewInit(): void {
    console.log('[CUSTOM HBC] ngAfterViewInit - skipping super to avoid parent filter$ conflict');
    // DO NOT call super.ngAfterViewInit() - it sets up a filter$ subscription
    // that processes the response with old logic and clears the chart

    // Trigger initial load since it was removed from parent
    // But only if we don't have a chart yet or it's not loading
    setTimeout(() => {
      const chartDom = document.getElementById(this.data?.widgetId);
      if (chartDom && !this.myChart) {
        console.log('[CUSTOM HBC] Triggering necessary initial load');
        this.loadAndRenderChart().pipe(takeUntil(this.unSubscribe$)).subscribe({
          next: (res: any) => {
            console.log('[CUSTOM HBC] Initial load success', res);

            // Read config keys from repower-screen-2.json
            const dataKey = this.data.dataKey || 'groupData';
            const valueKey = this.data.valueKey || 'count';
            const percentageKey = this.data.percentageKey || 'percentage';

            if (this.isEngineDowntimeChart) {
              const categoryKey = this.data.categoryKey || 'month_year';
              const downtime = this.resolveEngineDowntimeRecords(res);
              const records = downtime.data.filter((item: any) => `${item?.[categoryKey] ?? ''}`.trim());
              const categories = records.map((item: any) => `${item?.[categoryKey] ?? ''}`.trim());
              const values = records.map((item: any) => Number(item?.[valueKey] ?? 0));
              const sortedDowntime = this.sortEngineDowntimeByMonth(categories, values);

              const averageValue = downtime.average;
              this.displayData = averageValue !== null
                ? [{
                  category: this.getEngineUptimeAverageLabel(),
                  value: averageValue,
                  percentage: this.formatPercentageLabel(averageValue),
                  showBox: true
                }]
                : [];

              (this as any).engineDowntimeAverage = averageValue;

              if (categories.length === 0) {
                this.hasError = true;
                this.errorMessage = 'No data currently available';
              } else {
                this.hasError = false;
                const reversedCategories = [...sortedDowntime.categories].reverse();
                const reversedValues = [...sortedDowntime.values].reverse();
                this.renderChart({ category: reversedCategories, value: reversedValues });
                this.initializeResponsiveBindings();
              }
              return;
            }

            const resolvedRecord = this.resolveFleetLifeRecord(res);

            // For FleetLifeStageService, response may be an array of engine records.
            // Resolve the record matching current navigation engine.
            const groupData = resolvedRecord?.[dataKey];
            // Try to get total from groupData first, else fallback to res property
            let totalNoEngines = resolvedRecord?.total_no_engines || 0;
            if (groupData && groupData['total_no_engines'] && groupData['total_no_engines'][valueKey]) {
              totalNoEngines = groupData['total_no_engines'][valueKey];
            }

            if (groupData) {
              console.log('[CUSTOM HBC] ========== FLEET LIFE STAGE SORT DEBUG START ==========');
              console.log('[CUSTOM HBC] Found groupData:', groupData);
              const categories: string[] = [];
              const values: number[] = [];

              // Build flat arrays for chart (all items) & displayData for boxes (only with percentage)
              const allItems: { key: string; count: number; percentage?: number }[] = [];
              this.displayData = [];

              let newCount = 0;
              let rebuildCount = 0;

              Object.keys(groupData).forEach(key => {
                // Summary keys are represented inside the total stacked bar, not as separate rows.
                if (this.isFleetSummaryKey(key)) {
                  const normalizedKey = this.normalizeEngineKey(key);
                  if (normalizedKey === 'new' || normalizedKey === 'new_engines') {
                    newCount = groupData[key]?.[valueKey] || 0;
                  }
                  if (
                    normalizedKey === 'rebuild' ||
                    normalizedKey === 'rebuilt' ||
                    normalizedKey === 'rebuilt_engines'
                  ) {
                    rebuildCount = groupData[key]?.[valueKey] || 0;
                  }
                  return;
                }

                const item = groupData[key];
                const count = item[valueKey] || 0;
                categories.push(key);
                values.push(count);

                // Use backend percentage directly if available
                const backendPercentage = item?.[percentageKey];
                const hasPercentage = backendPercentage !== undefined && backendPercentage >= 0;
                this.displayData.push({
                  category: key,
                  value: count,
                  percentage: hasPercentage ? `${backendPercentage}%` : '',
                  showBox: hasPercentage
                });
              });

              // Sort Fleet Life Stage categories - always attempt to sort mapped categories
              console.log('[CUSTOM HBC] Before sorting - categories:', categories);
              const sorted = this.sortFleetLifeStageCategories(categories, values);
              console.log('[CUSTOM HBC] After sorting - categories:', sorted.categories);
              
              // Reverse for horizontal bar chart (ECharts renders bottom-to-top)
              categories.length = 0;
              categories.push(...sorted.categories.reverse());
              values.length = 0;
              values.push(...sorted.values.reverse());
              console.log('[CUSTOM HBC] After reverse - categories:', categories);

              // Re-sort displayData to match sorted categories
              this.displayData.sort((a, b) => {
                const orderA = this.getFleetLifeStageOrder(a.category);
                const orderB = this.getFleetLifeStageOrder(b.category);
                return orderA - orderB;
              });
              console.log('[CUSTOM HBC] Sorted displayData:', this.displayData);

              const isFailure = this.isFailureDetailsChart;

              if (!isFailure) {
                // Insert Total No Engines at END (renders at top due to reversal)
                categories.push('Total No Engines');
                values.push(totalNoEngines);
                // Add to displayData - use backend percentage if available, else calculate
                const totalEngineBackendPercentage = groupData?.['total_no_engines']?.[percentageKey];
                const totalEnginehasPercentage = totalEngineBackendPercentage !== undefined && totalEngineBackendPercentage >= 0;
                this.displayData.push({
                  category: 'Total No Engines',
                  value: totalNoEngines,
                  percentage: totalEnginehasPercentage ? `${totalEngineBackendPercentage}%` : '',
                  showBox: totalEnginehasPercentage
                });

                // Store division for Total No Engines using explicit backend counts.
                const resolvedNewCount = newCount > 0 || totalNoEngines === 0
                  ? newCount
                  : Math.max(totalNoEngines - rebuildCount, 0);
                (this as any).totalEngineDivision = {
                  new: resolvedNewCount,
                  rebuild: rebuildCount,
                  total: totalNoEngines
                };
              }

              console.log('[CUSTOM HBC] categories:', categories);
              console.log('[CUSTOM HBC] values:', values);
              console.log('[CUSTOM HBC] displayData:', this.displayData);

              if (categories.length <= 1) { // 1 because 'Total No Engines' is always added
                this.hasError = true;
                this.errorMessage = 'No data currently available';
              } else {
                this.hasError = false;
                // Pass flat structure to renderChart (it expects { category: [], value: [] })
                this.renderChart({ category: categories, value: values });
                this.initializeResponsiveBindings();
              }
            } else {
              this.hasError = true;
              this.errorMessage = 'No data available';
            }
          }
        });
      }
    }, 100);
  }

  protected override loadAndRenderChart(filters?: any): any {
    console.log('[CUSTOM HBC] loadAndRenderChart', filters);
    this.currentFilters = filters;
    const chartDom = document.getElementById(this.data?.widgetId);
    console.log('[CUSTOM HBC] loadAndRenderChart - chartDom:', chartDom ? 'found' : 'NOT FOUND', this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      console.log('[CUSTOM HBC] Initializing ECharts');
      const height = this.data.chartConfig?.height || this.data.height || '400px';
      chartDom.style.height = height;
      this.myChart = echarts.init(chartDom);
    } else {
      console.log('[CUSTOM HBC] ECharts already initialized');
    }

    this.myChart.showLoading();

    // Get dynamic payload data from router state
    const navState = window.history.state || {};

    console.log('[CUSTOM HBC] Router state from history:', navState);

    let mineSite = navState?.mine_site || navState?.selectedMineName || '';
    let engineModel = navState?.engine_model || navState?.selectedEngineModelKey || navState?.selectedEngine || '';
    const rawEngineModel = engineModel;

    console.log('[CUSTOM HBC] Extracted - mineSite:', mineSite, 'engineModel:', engineModel);

    engineModel = this.isEngineDowntimeChart ? engineModel : this.formatFleetEngineModel(engineModel);
    console.log('[CUSTOM HBC] Formatted engineModel:', engineModel);

    // Build dynamic payload if we have the data
    let customData = JSON.parse(JSON.stringify(this.data || {}));

    let isFailureDetails = false;
    const ep = customData?.customPayload?.endpoints || customData?.endpoints || customData;
    if (Array.isArray(ep)) {
      isFailureDetails = ep.some((e: any) => e.functionName === 'FailureDetails');
    } else if (ep?.functionName === 'FailureDetails') {
      isFailureDetails = true;
    }

    if (mineSite || engineModel) {
      console.log('[CUSTOM HBC] Found dynamic data - mineSite:', mineSite, 'engineModel:', engineModel);

      if (!customData.customPayload) {
        customData.customPayload = {};
      }
      if (!customData.customPayload.requestData) {
        customData.customPayload.requestData = {};
      }

      if (this.isEngineDowntimeChart) {
        const filter = customData.customPayload.requestData.filter || {};
        const splitEngine = this.splitEngineEmission(rawEngineModel || filter.engine_model);
        delete filter.site;
        delete filter.country;
        delete filter.clean_equipment_model;
        delete filter.engine_model_key;
        delete filter.mine_site;
        delete filter.region;
        delete filter.customer;
        customData.customPayload.requestData.filter = {
          ...filter,
          ...(mineSite ? { mine: mineSite } : {}),
          ...(navState?.selectedTruckModel || navState?.machine_model ? {
            machine_model: navState?.selectedTruckModel || navState?.machine_model
          } : {}),
          ...(splitEngine.engineModel ? { engine_model: splitEngine.engineModel } : {}),
          ...(splitEngine.emission ? { emission: splitEngine.emission } : {})
        };
        delete customData.customPayload.requestData.site;
        delete customData.customPayload.requestData.country;
        delete customData.customPayload.requestData.clean_equipment_model;
        delete customData.customPayload.requestData.engine_model_key;
        delete customData.customPayload.requestData.region;
      } else {
        // Update the customPayload with dynamic values
        if (mineSite) {
          customData.customPayload.requestData.mine_site = mineSite;
        }
        if (engineModel && !isFailureDetails) {
          customData.customPayload.requestData.engine_model = engineModel;
        }
      }

      if (isFailureDetails && customData.customPayload.requestData) {
        delete customData.customPayload.requestData.engine_model;
        delete customData.customPayload.requestData.engine_model_key;
        delete customData.customPayload.requestData.mine;
        delete customData.customPayload.requestData.region;
      }

      console.log('[CUSTOM HBC] Updated customPayload:', customData.customPayload);
    } else {
      console.log('[CUSTOM HBC] No dynamic data found, using default payload from config');
    }

    const cleanFilters = { ...filters, useQueryParams: this.data?.useQueryParams || false } as any;
    if (isFailureDetails) {
      delete cleanFilters.engine_model;
      delete cleanFilters.engine_model_key;
      delete cleanFilters.mine;
      delete cleanFilters.region;
    }
    if (this.isEngineDowntimeChart) {
      delete cleanFilters.engine_model_key;
      delete cleanFilters.mine_site;
      delete cleanFilters.site;
      delete cleanFilters.country;
      delete cleanFilters.clean_equipment_model;
      delete cleanFilters.region;
    }

    return this.configService.getConfig(
      this.data.apiUrl,
      customData,  // Use updated data with dynamic payload
      cleanFilters,
      this.data.method
    ).pipe(takeUntil(this.unSubscribe$));
  }

  protected override renderChart(chartData?: any): any {
    console.log('[CUSTOM HBC] renderChart', chartData);
    if (!this.data || !chartData || !chartData.category || chartData.category.length === 0) {
      console.warn('[CUSTOM HBC] Missing data or config', { data: this.data, chartData });
      this.hasError = true;
      this.errorMessage = 'No data currently available';
      return;
    }

    // Reset error state if data is present
    this.hasError = false;

    // displayData is already populated in ngAfterViewInit subscriber with real percentages
    // Only recalculate if it wasn't set (fallback for flat data)
    if (this.displayData.length === 0) {
      const total = (chartData.value || []).reduce((acc: number, val: number) => acc + val, 0);
      this.displayData = (chartData.category || []).map((cat: string, index: number) => {
        const val = (chartData.value || [])[index];
        const percent = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
        return { category: cat, value: val, percentage: `${percent}%`, showBox: true };
      });
    }
    console.log('[CUSTOM HBC] displayData', this.displayData);

    const extendLeft = this.data.orientation === 'left';

    // Set dynamic height if provided
    const chartDom = document.getElementById(this.data.widgetId);
    console.log('[CUSTOM HBC] renderChart - chartDom:', chartDom ? 'found' : 'NOT FOUND', {
      width: chartDom?.clientWidth,
      height: chartDom?.clientHeight
    });
    if (chartDom) {
      const height = this.data.chartConfig?.height || this.data.height || '400px';
      chartDom.style.height = height;
    }
    const containerWidth = chartDom?.clientWidth || 0;
    const compactLayout = containerWidth > 0 && containerWidth < 1200;
    const axisNameFontSize = this.data?.chartConfig?.axisNameFontSize ?? (compactLayout ? 14 : 18);
    const yAxisLabelFontSize = this.data?.chartConfig?.yAxisLabelFontSize ?? (compactLayout ? 13 : 16);
    const valueLabelFontSize = this.data?.chartConfig?.valueLabelFontSize ?? (compactLayout ? 14 : 18);
    const yAxisNameGap = this.data?.chartConfig?.nameGap ?? (compactLayout ? 145 : 170);
    const gridLeft = this.data?.chartConfig?.gridLeft ?? (compactLayout ? 180 : 220);
    const gridRight = this.data?.chartConfig?.gridRight ?? 40;
    const gridTop = this.data?.chartConfig?.gridTop ?? '10%';
    const gridBottom = this.data?.chartConfig?.gridBottom ?? '12%';
    const barWidth = this.data?.chartConfig?.barWidth;

    const option: any = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) params = [params];
          const firstParam = params[0];
          const category = firstParam?.name || '';

          if (category === 'Total No Engines' && (this as any).totalEngineDivision) {
            const div = (this as any).totalEngineDivision;
            return `<strong>Total No Engines (${div.total})</strong><br/>New: ${div.new}<br/>Rebuilt: ${div.rebuild}`;
          }

          const value = firstParam?.value || 0;
          const tooltipLabels = this.data?.tooltipLabels || {};
          const categoryLabel = tooltipLabels.category || 'Category';
          const valueLabel = tooltipLabels.value || 'Value';
          const formattedValue = this.data?.useCommaStat
            ? formatDecimalWithCommas(value)
            : value;
          return `${categoryLabel}: ${category}<br/>${valueLabel}: ${formattedValue}`;
        }
      },
      grid: {

        left: gridLeft, // space for y-axis category labels
        right: gridRight,

        top: gridTop,
        bottom: gridBottom,
        containLabel: false
      },
      xAxis: {
        type: 'value',
        inverse: extendLeft,
        name: this.isFailureDetailsChart
          ? 'Count of Failure Engines'
          : (this.isEngineDowntimeChart ? this.getEngineUptimeAxisLabel() : (this.data?.chartConfig?.xAxisName || 'Engines')),
        nameLocation: 'middle',
        nameGap: this.isFailureDetailsChart ? 5 : 35,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: axisNameFontSize,
          color: '#1a1a1a'
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'category',
        inverse: false,
        name: this.isFailureDetailsChart ? 'Year' : (this.data?.chartConfig?.yAxisName || 'Engine Run Hours'),
        nameLocation: 'middle',
        nameGap: yAxisNameGap,
        nameRotate: 90, // Rotates it vertically
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: axisNameFontSize,
          color: '#1a1a1a'
        },
        data: chartData?.category || [],
        axisLabel: {
          fontWeight: 'bold', // Matching line chart legends
          fontSize: yAxisLabelFontSize,
          color: '#1a1a1a', // Distinct dark color
          fontFamily: 'sans-serif',
          formatter: (value: string) => {
            const label = value === 'total_no_engines' ? 'Total No Engines' : value;
            return label.length > 30 ? label.slice(0, 30) + '…' : label;
          }
        }
      },
      series: [
        {
          type: 'bar',
          stack: 'engines',
          ...(barWidth !== undefined ? { barWidth } : {}),
          label: { show: true },
          data: (chartData?.category || []).map((cat: any, index: any) => {
            const value = chartData?.value[index];
            const isSelected = this.selectedCategory === null || this.selectedCategory === cat;
            const isTotalEngines = cat === 'Total No Engines';

            if (isTotalEngines) {
              return {
                value: 0,
                itemStyle: { color: 'transparent' },
                label: { show: false }
              };
            }

            return {
              value,
              itemStyle: {
                color: isSelected
                  ? '#b6d79b'
                  : 'rgba(200, 200, 200, 0.3)'
              },
              label: {
                show: true,
                position: 'right',
                distance: 10,
                color: 'black',
                fontWeight: 'bold',
                fontSize: valueLabelFontSize,
                formatter: ({ value }: any) => this.isEngineDowntimeChart
                  ? this.formatPercentageLabel(Number(value))
                  : formatStat(value)
              }
            };
          }) || []
        },
        {
          name: 'New',
          type: 'bar',
          stack: 'engines',
          ...(barWidth !== undefined ? { barWidth } : {}),
          data: (chartData?.category || []).map((cat: any) => {
            if (cat === 'Total No Engines' && (this as any).totalEngineDivision) {
              return (this as any).totalEngineDivision.new;
            }
            return 0;
          }),
          itemStyle: { color: this.data.barColor || '#91CC75' },
          label: { show: false }
        },
        {
          name: 'Rebuilt',
          type: 'bar',
          stack: 'engines',
          ...(barWidth !== undefined ? { barWidth } : {}),
          data: (chartData?.category || []).map((cat: any) => {
            if (cat === 'Total No Engines' && (this as any).totalEngineDivision) {
              return (this as any).totalEngineDivision.rebuild;
            }
            return 0;
          }),
          itemStyle: { color: '#FF9800' },
          label: {
            show: true,
            position: 'right',
            distance: 10,
            color: 'black',
            fontWeight: 'bold',
            fontSize: valueLabelFontSize,
            formatter: (params: any) => {
              if (params.name === 'Total No Engines' && (this as any).totalEngineDivision) {
                return formatStat((this as any).totalEngineDivision.total);
              }
              return '';
            }
          }
        },
      ]
    };

    if (this.isEngineDowntimeChart) {
      option.series = [option.series[0]];
    }

    if (this.myChart) {
      this.myChart.setOption(option, true); // true to clear previous configuration
      this.syncPercentageBoxPositions();
      this.myChart.off('mouseover');
      this.myChart.off('mouseout');

      setTimeout(() => {
        this.myChart.resize();
        this.syncPercentageBoxPositions();
        this.myChart.hideLoading();
      }, 500);
    }
  }

  getPercentageBoxTop(category: string): number | null {
    const top = this.boxYPositions[category];
    return Number.isFinite(top) ? top : null;
  }

  private syncPercentageBoxPositions(): void {
    if (!this.myChart || !this.displayData?.length) {
      this.boxYPositions = {};
      this.hasPixelBoxPositions = false;
      return;
    }

    const nextPositions: Record<string, number> = {};
    for (const item of this.displayData) {
      const y = this.myChart.convertToPixel({ yAxisIndex: 0 }, item.category);
      if (typeof y === 'number' && Number.isFinite(y)) {
        nextPositions[item.category] = y;
      }
    }

    this.boxYPositions = nextPositions;
    this.hasPixelBoxPositions = Object.keys(nextPositions).length > 0;
  }

  private initializeResponsiveBindings(): void {
    if (this.responsiveBindingsInitialized) {
      return;
    }

    this.addResizeListener();
    this.reflowSub = this.responsiveLayoutService.reflow$.pipe(takeUntil(this.unSubscribe$)).subscribe(() => {
      this.myChart?.resize();
      this.syncPercentageBoxPositions();
    });
    this.responsiveBindingsInitialized = true;
  }

  protected override setupChartClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupChartClickEvent();
  }

  protected override handleResize = (): void => {
    this.myChart?.resize();
    this.syncPercentageBoxPositions();
  };

  protected override addResizeListener(): any {
    if (this.responsiveBindingsInitialized) {
      return;
    }
    return super.addResizeListener();
  }
}
